import React from 'react';
import { useNavigate, useParams } from 'react-router';

import { useWrapRequest } from '@components/hooks/use-wrap-request';
import { Spinner } from '@components/spinner';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Api } from '@core/api';
import { links } from '@core/router';
import { toast } from '@core/toast';
import { injectTSDI } from '@core/tsdi';

import {
    CalculatedMetricDefinitionForm,
    CalculatedMetricDefinitionFormData,
    toFormData,
    toUpdatePayload
} from './calculated-metric-definition-form';

export const EditCalculatedMetricDefinition = () => {
    const { definitionId } = useParams();
    const navigate = useNavigate();
    const { fitnessActivityClient } = injectTSDI(Api);

    const definition = useWrapRequest(async (id: number) => {
        const result =
            await fitnessActivityClient.admin.workoutCalculatedMetricDefinitionControllerGetById(
                id
            );
        return result.data;
    });

    React.useEffect(() => {
        if (definitionId) {
            definition.request([Number(definitionId)]);
        }
    }, [definitionId]);

    const handleSubmit = async (
        formData: CalculatedMetricDefinitionFormData
    ) => {
        await fitnessActivityClient.admin.workoutCalculatedMetricDefinitionControllerUpdate(
            Number(definitionId),
            toUpdatePayload(formData)
        );
        toast.success('Calculated metric updated');
        navigate(links.calculatedMetricDefinitions());
    };

    if (definition.loading || (!definition.result && !definition.error)) {
        return (
            <div className="flex min-h-[400px] items-center justify-center">
                <Spinner />
            </div>
        );
    }

    if (definition.error || !definition.result) {
        return (
            <Alert variant="destructive">
                <AlertDescription>
                    Failed to load the calculated metric.
                </AlertDescription>
            </Alert>
        );
    }

    return (
        <CalculatedMetricDefinitionForm
            title="Edit Calculated Metric"
            submitLabel="Save"
            submittingLabel="Saving..."
            initialData={toFormData(definition.result)}
            metricKeyLocked
            onCancel={() => navigate(links.calculatedMetricDefinitions())}
            onSubmit={handleSubmit}
        />
    );
};
